/**
 * Hook for calculating floating dropdown position
 * Handles position calculation for floating variant
 */

import React from "react";
import type { DropdownPosition } from "@/lib/types/select-components";

interface UseResponsiveSelectPositionProps {
  expandInline: boolean;
  expandInlineVariant: "replace" | "floating";
  open: boolean;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Optional — see ResponsiveCommandSelectProps.maxDropdownWidth for full docs */
  maxDropdownWidth?: number;
}

export function useResponsiveSelectPosition({
  expandInline,
  expandInlineVariant,
  open,
  triggerRef,
  maxDropdownWidth,
}: UseResponsiveSelectPositionProps) {
  const [dropdownPosition, setDropdownPosition] = React.useState<DropdownPosition | null>(null);

  React.useEffect(() => {
    if (!expandInline || expandInlineVariant !== "floating" || !open || !triggerRef.current) return;

    const calculatePosition = () => {
      if (!triggerRef.current) return;
      
      const rect = triggerRef.current.getBoundingClientRect();
      const rawWidth = Math.max(rect.width, 280);
      const width = maxDropdownWidth != null ? Math.min(rawWidth, maxDropdownWidth) : rawWidth;

      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width,
      });
    };

    // Calculate immediately
    calculatePosition();

    // Recalculate on scroll or resize
    const handleScroll = () => calculatePosition();
    const handleResize = () => calculatePosition();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      setDropdownPosition(null);
    };
  }, [expandInline, expandInlineVariant, open, triggerRef, maxDropdownWidth]);

  return dropdownPosition;
}
